import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isString } from 'class-validator';

import { useEventHub } from '@satellite/composables/useEventHub';
import yardSpotService from '@satellite/services/yardSpot-service';
import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import {
  EntityPresenterV2Mode,
  isEntityPresenterV2QueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterV2EntityMode';

import type { ComputedRef, Ref } from 'vue';
import type { QueryFunctionContext } from '@tanstack/query-core';
import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from './presenterV2Placeholder';
import type { YardSpotDetails } from './yardSpotPresenterV2Shared';

/**
 * Fetches a single spot's details using the id encoded in the query key.
 *
 * This is intentionally defined at module scope (not inside `useYardSpotFetcherV2`) so the
 * cached `queryFn` closure captures nothing from the component setup scope. TanStack retains
 * the `queryFn` for `gcTime` after a card unmounts; a setup-scoped closure would keep the whole
 * `setup()` context alive (V8 allocates one shared `Context` per scope, so any closure defined
 * there transitively pins every context-allocated variable, e.g. `effectiveSpotId`), which in
 * turn pins the component instance -> subTree -> detached DOM. Hoisting removes that retention.
 *
 * The spot id is the segment before the `'detail'` suffix in `queryKeys.spotDetail(id)`.
 */
async function fetchYardSpotDetailFromKey({
  signal,
  queryKey
}: QueryFunctionContext): Promise<YardSpotDetails | null> {
  const spotIdFromKey = queryKey[queryKey.length - 2];
  if (!isString(spotIdFromKey) || !spotIdFromKey) {
    return null;
  }

  const response = await yardSpotService.getSpotDetails<unknown, YardSpotDetails>(spotIdFromKey, {
    requestConfig: {
      signal,
      params: {}
    },
    enableCache: true
  });

  return response;
}

export interface UseYardSpotFetcherV2Options {
  /**
   * Spot ID to fetch. Can be a ref for reactivity.
   * Optional if initialSpotData (with id) is provided.
   */
  spotId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial spot data used as initialData for the query.
   * Effective id is derived from spotId or initialSpotData.id (explicit spotId wins; mismatch triggers a warning).
   */
  initialSpotData?:
    | Ref<Maybe<YardSpotDetails>>
    | ComputedRef<Maybe<YardSpotDetails>>
    | YardSpotDetails;

  /**
   * Optional incomplete spot data shown as placeholderData while the query fetches.
   */
  placeholderSpotData?:
    | Ref<Maybe<PresenterPlaceholderData<YardSpotDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardSpotDetails>>>
    | PresenterPlaceholderData<YardSpotDetails>;

  /**
   * When true, the query runs without viewport gating.
   * Default: false
   */
  headless?: boolean;

  /**
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: no fetch until first matching EventHub event, refresh(), or invalidate()
   * - `static`: no fetch/subscriptions; returns staticSpotData only
   */
  mode?: EntityPresenterV2Mode;

  /**
   * Data source for `mode: 'static'`.
   */
  staticSpotData?:
    | Ref<Maybe<YardSpotDetails>>
    | ComputedRef<Maybe<YardSpotDetails>>
    | YardSpotDetails;

  staleTime?: number;

  onUpdateReceived?: (spot: YardSpotDetails) => void;
}

export function useYardSpotFetcherV2(options: UseYardSpotFetcherV2Options) {
  const {
    spotId,
    initialSpotData,
    placeholderSpotData,
    headless = false,
    mode = EntityPresenterV2Mode.Live,
    staticSpotData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveSpotId = computed((): string | null => {
    const explicitId = unref(spotId);
    const fromObject = toValue(initialSpotData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardSpotFetcherV2] spotId mismatch:',
          `explicit spotId (${explicitId}) does not match spot.id (${fromObject}). Using explicit spotId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const initialSpot = computed(() => toValue(initialSpotData) ?? undefined);
  const placeholderSpot = computed(() => toValue(placeholderSpotData) ?? undefined);
  const staticSpot = computed(() => toValue(staticSpotData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (toValue(initialSpotData) != null || toValue(placeholderSpotData) != null)
  ) {
    console.warn(
      '[useYardSpotFetcherV2] mode=static ignores initialSpotData/placeholderSpotData. Use staticSpotData.'
    );
  }

  if (isLazyLiveMode(mode) && toValue(initialSpotData) != null) {
    console.warn(
      '[useYardSpotFetcherV2] mode=lazy-live does not use initialSpotData for the query cache; use placeholderSpotData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const spotQuery = useQuery({
    queryKey: computed(() =>
      effectiveSpotId.value ? queryKeys.spotDetail(effectiveSpotId.value) : []
    ),
    queryFn: fetchYardSpotDetailFromKey,
    enabled: computed(() =>
      isEntityPresenterV2QueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveSpotId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : initialSpot.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderSpot.value as YardSpotDetails | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false
  });

  const spot = computed(() => {
    if (isStaticMode(mode)) {
      if (!staticSpot.value) {
        console.warn('[useYardSpotFetcherV2] mode=static requires staticSpotData.');
        return null;
      }
      return staticSpot.value;
    }
    return spotQuery.data.value || null;
  });

  const spotAssignment = computed(() => spot.value?.spotAssignments?.[0] ?? null);
  const assetVisitId = computed(() => spotAssignment.value?.assetVisitId);
  const assetContainerId = computed(() => spotAssignment.value?.assetContainerId);

  const isLoading = computed(() => (isStaticMode(mode) ? false : spotQuery.isLoading.value));
  const isFetching = computed(() => (isStaticMode(mode) ? false : spotQuery.isFetching.value));
  const error = computed(() => (isStaticMode(mode) ? null : spotQuery.error.value));

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    if (effectiveSpotId.value) {
      queryClient.invalidateQueries(
        {
          queryKey: queryKeys.spot(effectiveSpotId.value)
        },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    await nextTick();
    await spotQuery.refetch();
  };

  const deferOrInvalidate = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleSpotEvent = (event: { id?: string }) => {
    if (event.id !== effectiveSpotId.value) {
      return;
    }

    deferOrInvalidate();
  };

  const handleSpotAssignmentEvent = (event: {
    spotId?: string;
    assetContainerId?: string;
    assetVisitId?: string;
  }) => {
    const matchesSpot = event.spotId === effectiveSpotId.value;
    const matchesContainer =
      assetContainerId.value != null && event.assetContainerId === assetContainerId.value;

    const matchesVisit = assetVisitId.value != null && event.assetVisitId === assetVisitId.value;
    if (!matchesSpot && !matchesContainer && !matchesVisit) {
      return;
    }

    deferOrInvalidate();
  };

  const handleYardTaskEvent = (event: {
    type: string;
    child: { originSpotId?: string; destinationSpotId?: string };
  }) => {
    const isRelatedSpot = [event?.child?.originSpotId, event?.child?.destinationSpotId].includes(
      effectiveSpotId.value
    );
    if (event?.type !== 'move' || !isRelatedSpot) {
      return;
    }

    deferOrInvalidate();
  };

  const handleAssetContainerEventEvent = (event: {
    assetContainerId?: string;
    assetVisitId?: string;
  }) => {
    const matchesContainer =
      assetContainerId.value != null && event.assetContainerId === assetContainerId.value;
    const matchesVisit = assetVisitId.value != null && event.assetVisitId === assetVisitId.value;

    if (!matchesContainer && !matchesVisit) {
      return;
    }

    deferOrInvalidate();
  };

  const handleAssetContainerEvent = (event: { id?: string; assetVisitId?: string }) => {
    const matchesContainer = assetContainerId.value != null && event.id === assetContainerId.value;
    const matchesVisit = assetVisitId.value != null && event.assetVisitId === assetVisitId.value;

    if (!matchesContainer && !matchesVisit) {
      return;
    }

    deferOrInvalidate();
  };

  const handleAssetVisitEventEvent = (event: {
    assetContainerId?: string;
    assetVisitId?: string;
  }) => {
    const matchesContainer =
      assetContainerId.value != null && event.assetContainerId === assetContainerId.value;
    const matchesVisit = assetVisitId.value != null && event.assetVisitId === assetVisitId.value;

    if (!matchesContainer && !matchesVisit) {
      return;
    }

    deferOrInvalidate();
  };

  watch(spot, newSpot => {
    if (onUpdateReceived && newSpot) {
      onUpdateReceived(newSpot);
    }
  });

  watch(effectiveSpotId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.on('update-Spot', handleSpotEvent);
      eventHub.on('delete-Spot', handleSpotEvent);

      eventHub.on('create-SpotAssignment', handleSpotAssignmentEvent);
      eventHub.on('update-SpotAssignment', handleSpotAssignmentEvent);
      eventHub.on('delete-SpotAssignment', handleSpotAssignmentEvent);

      eventHub.on('create-YardTask', handleYardTaskEvent);
      eventHub.on('update-YardTask', handleYardTaskEvent);
      eventHub.on('delete-YardTask', handleYardTaskEvent);

      eventHub.on('create-AssetContainerEvent', handleAssetContainerEventEvent);
      eventHub.on('update-AssetContainerEvent', handleAssetContainerEventEvent);
      eventHub.on('delete-AssetContainerEvent', handleAssetContainerEventEvent);

      eventHub.on('update-AssetContainer', handleAssetContainerEvent);

      eventHub.on('create-AssetVisitEvent', handleAssetVisitEventEvent);
      eventHub.on('update-AssetVisitEvent', handleAssetVisitEventEvent);
      eventHub.on('delete-AssetVisitEvent', handleAssetVisitEventEvent);
    }
  });

  onScopeDispose(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.off('update-Spot', handleSpotEvent);
      eventHub.off('delete-Spot', handleSpotEvent);

      eventHub.off('create-SpotAssignment', handleSpotAssignmentEvent);
      eventHub.off('update-SpotAssignment', handleSpotAssignmentEvent);
      eventHub.off('delete-SpotAssignment', handleSpotAssignmentEvent);

      eventHub.off('create-YardTask', handleYardTaskEvent);
      eventHub.off('update-YardTask', handleYardTaskEvent);
      eventHub.off('delete-YardTask', handleYardTaskEvent);

      eventHub.off('create-AssetContainerEvent', handleAssetContainerEventEvent);
      eventHub.off('update-AssetContainerEvent', handleAssetContainerEventEvent);
      eventHub.off('delete-AssetContainerEvent', handleAssetContainerEventEvent);

      eventHub.off('update-AssetContainer', handleAssetContainerEvent);

      eventHub.off('create-AssetVisitEvent', handleAssetVisitEventEvent);
      eventHub.off('update-AssetVisitEvent', handleAssetVisitEventEvent);
      eventHub.off('delete-AssetVisitEvent', handleAssetVisitEventEvent);
    }
  });

  return {
    data: spot,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    onIntersect: handleIntersect,

    query: spotQuery
  };
}
